import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../Auth/AuthContext';
import axios from 'axios';
import './ReviseBill.css';
import { useNavigate, useLocation } from "react-router-dom";
import config from '../../config';
import GenerateRevisedBillModal from './GenerateRevisedBillModal';

const listReviseBillUrl = `${config.BASE_URL}/list-to-revise-bill`;
const downloadPdfUrl = `${config.BASE_URL}/generate-pdf-revised-bill`;
const updateOneReviseBill = `${config.BASE_URL}/update-one-revise-bill`;

const ReviseBill = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const { username, userProfile, moduleChange, setModuleChange, setTripIDState, TripIDState, activeModule, setActiveModule, setSearchValue, reviseBillState, setReviseBillState } = useAuth();

  // Added flag to control initial data loading
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    bill_id: ''
  });
  const [tableData, setTableData] = useState({ billed: [], acknowledged: [] });
  const [noDataMessage, setNoDataMessage] = useState('');
  const [selectedBilled, setSelectedBilled] = useState([]);
  const [selectedAcknowledged, setSelectedAcknowledged] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showBillModal, setShowBillModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const selectAllBilledRef = useRef(null);
  const selectAllAckRef = useRef(null);

  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editingType, setEditingType] = useState(null); // 'billed' or 'acknowledged'

  // References for edit form and edit containers
  const editRef = useRef(null);
  const editFormRef = useRef(null);

  // References for table scroll containers
  const billedTableRef = useRef(null);
  const acknowledgedTableRef = useRef(null);

  // Parse URL for bill_id param that might be passed, but only once on initial mount
  const [searchProcessed, setSearchProcessed] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('actMod', 'revise-bill');
    setActiveModule('revise-bill');

    const searchParams = new URLSearchParams(location.search);
    const billIdFromUrl = searchParams.get('bill_id');
    const wasSearchProcessed = sessionStorage.getItem('reviseBillSearchProcessed') === 'true';

    if (wasSearchProcessed) {
      // Search was already processed - just update filters
      setFilters(prev => ({
        ...prev,
        bill_id: billIdFromUrl || ''
      }));
      sessionStorage.removeItem('reviseBillSearchProcessed');
      setSearchProcessed(true);
    } else if (billIdFromUrl && !searchProcessed) {
      // Direct navigation with bill_id - fetch data
      setFilters(prev => ({
        ...prev,
        bill_id: billIdFromUrl
      }));
      fetchTableData({ bill_id: billIdFromUrl });
      setSearchProcessed(true);
    } else if (!billIdFromUrl) {
      // Normal navigation - reset state
      resetComponentState();
      setSearchProcessed(false);
    }

    return () => {
      // Cleanup when component unmounts
      sessionStorage.removeItem('reviseBillSearchProcessed');
    };
  }, [location]);

  // Reset function
  const resetComponentState = () => {
    setFilters({
      startDate: '',
      endDate: '',
      bill_id: ''
    });
    setTableData({ billed: [], acknowledged: [] });
    setNoDataMessage('');
    setSelectedBilled([]);
    setSelectedAcknowledged([]);
  };

  // Modify your existing useEffect that handles reviseBillState
  useEffect(() => {
    if (reviseBillState && searchProcessed) {
      const newTableData = {
        billed: reviseBillState.billed || [],
        acknowledged: reviseBillState.acknowledged || []
      };
      setTableData(newTableData);
      setSelectedBilled([]);
      setSelectedAcknowledged([]);

      if (newTableData.billed.length === 0 && newTableData.acknowledged.length === 0) {
        setNoDataMessage('No records available');
      } else {
        setNoDataMessage('');
      }
    }
  }, [reviseBillState, searchProcessed]);

  // This function handles fetching table data with provided filter parameters
  const fetchTableData = async (filterParams) => {
    try {
      // Create a clean filter object that only includes defined values
      const cleanFilters = {};

      // Add filters that are provided and not empty
      if (filterParams.startDate) cleanFilters.startDate = filterParams.startDate;
      if (filterParams.endDate) cleanFilters.endDate = filterParams.endDate;
      if (filterParams.bill_id) cleanFilters.bill_id = filterParams.bill_id;

      console.log("Fetching with filters:", cleanFilters);

      const response = await axios.post(
        listReviseBillUrl,
        cleanFilters,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          }
        }
      );

      console.log("API Response:", response.data);

      const resultData = {
        billed: response?.data?.billed || [],
        acknowledged: response?.data?.acknowledged || []
      };

      if (resultData.billed.length === 0 && resultData.acknowledged.length === 0) {
        setNoDataMessage(response.data.message || 'No records available');
      } else {
        setNoDataMessage('');
      }

      setTableData(resultData);

      // Update reviseBillState for consistency across the application
      setReviseBillState(response.data);

      // Reset selections when fetching new data
      setSelectedBilled([]);
      setSelectedAcknowledged([]);

    } catch (error) {
      if (error.response?.data?.message) {
        alert(error.response?.data?.message);
      } else {
        alert('Error fetching Table data');
      }
      console.error('Error fetching reports:', error);
    }
  };

  // const formatTime = (time) => {
  //   if (!time) return '';

  //   const [timePart, ampm] = time.split(' ');
  //   const [hour, minutes] = timePart.split(':');

  //   let formattedTime = `${hour}:${minutes} ${ampm}`;

  //   return formattedTime;
  // }

  useEffect(() => {
    if (selectAllBilledRef.current) {
      const isIndeterminate =
        selectedBilled.length > 0 &&
        selectedBilled.length < tableData.billed.length;
      selectAllBilledRef.current.indeterminate = isIndeterminate;
    }

    if (selectAllAckRef.current) {
      const isIndeterminate =
        selectedAcknowledged.length > 0 &&
        selectedAcknowledged.length < tableData.acknowledged.length;
      selectAllAckRef.current.indeterminate = isIndeterminate;
    }
  }, [selectedBilled, selectedAcknowledged, tableData]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === 'bill_id') {
      if (value === '') {
        setFilters({
          ...filters,
          bill_id: ''
        });
        return;
      }

      let formattedValue = value.toUpperCase().trim();
      if (!formattedValue.startsWith('B')) {
        formattedValue = 'B' + formattedValue;
      }

      const numbersOnly = formattedValue.substring(1).replace(/\D/g, '');
      formattedValue = 'B' + numbersOnly;

      setFilters({
        ...filters,
        bill_id: formattedValue
      });
    } else {
      setFilters({
        ...filters,
        [name]: value
      });
    }
  };

  // Process reviseBillState data when it becomes available
  const processReviseBillState = () => {
    if (reviseBillState) {
      const newTableData = {
        billed: reviseBillState.billed || [],
        acknowledged: reviseBillState.acknowledged || []
      };

      setTableData(newTableData);

      // Reset selections if data changes
      setSelectedBilled([]);
      setSelectedAcknowledged([]);

      if (newTableData.billed.length === 0 && newTableData.acknowledged.length === 0) {
        setNoDataMessage('No records available');
      } else {
        setNoDataMessage('');
      }

      // If there's bill data and we find a billing_id in the first record,
      // update the filters to match the current bill_id
      if (newTableData.billed.length > 0 && newTableData.billed[0].billing_id) {
        setFilters(prev => ({
          ...prev,
          bill_id: newTableData.billed[0].billing_id
        }));
      } else if (newTableData.acknowledged.length > 0 && newTableData.acknowledged[0].billing_id) {
        setFilters(prev => ({
          ...prev,
          bill_id: newTableData.acknowledged[0].billing_id
        }));
      }
    }
  };

  // Changed useEffect to only process reviseBillState when it changes
  // and we explicitly want to update the data (like after an action)
  useEffect(() => {
    setSearchValue('');

    // We only process the reviseBillState when it changes if we're not in the initial mounting phase
    // This prevents the unnecessary API call when navigating back to this component
    if (reviseBillState && initialLoadDone) {
      processReviseBillState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviseBillState]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if ((filters.startDate && filters.endDate)) {
      if (new Date(filters.startDate) > new Date(filters.endDate)) {
        alert("End date should be greater than or equal to Start Date.");
        return;
      }
    }

    if (((filters.startDate && !filters.endDate) || (!filters.startDate && filters.endDate)) && filters.bill_id) {
      alert('Please select both start-date and end-date');
      return;
    }

    // If no filters are selected, try to use the last known bill_id from editing or selected rows
    let effectiveFilters = { ...filters };

    // If no filters are provided but we have an editing row with a bill_id, use that bill_id
    if (!filters.startDate && !filters.endDate && !filters.bill_id && editingData?.billing_id) {
      effectiveFilters.bill_id = editingData.billing_id;
    }

    // If we still have no filters and have selected rows, try to use the bill_id from the first selected row
    if (!effectiveFilters.startDate && !effectiveFilters.endDate && !effectiveFilters.bill_id) {
      if (selectedBilled.length > 0) {
        const selectedRow = tableData.billed.find(row => row._id === selectedBilled[0]);
        if (selectedRow?.billing_id) {
          effectiveFilters.bill_id = selectedRow.billing_id;
        }
      } else if (selectedAcknowledged.length > 0) {
        const selectedRow = tableData.acknowledged.find(row => row._id === selectedAcknowledged[0]);
        if (selectedRow?.billing_id) {
          effectiveFilters.bill_id = selectedRow.billing_id;
        }
      }
    }

    // Finally, if we still have no filters but have current table data, use the first row's bill_id
    if (!effectiveFilters.startDate && !effectiveFilters.endDate && !effectiveFilters.bill_id) {
      if (tableData.billed.length > 0 && tableData.billed[0].billing_id) {
        effectiveFilters.bill_id = tableData.billed[0].billing_id;
      } else if (tableData.acknowledged.length > 0 && tableData.acknowledged[0].billing_id) {
        effectiveFilters.bill_id = tableData.acknowledged[0].billing_id;
      }
    }

    await fetchTableData(effectiveFilters);
  };

  const handleCheckboxChange = (id, isBilled) => {
    if (isBilled) {
      setSelectedBilled(prev =>
        prev.includes(id) ? prev.filter(_id => _id !== id) : [...prev, id]
      );
    } else {
      setSelectedAcknowledged(prev =>
        prev.includes(id) ? prev.filter(_id => _id !== id) : [...prev, id]
      );
    }
  };

  const handleSelectAllBilled = (checked) => {
    if (checked) {
      setSelectedBilled(tableData.billed.map(item => item._id));
    } else {
      setSelectedBilled([]);
    }
  };

  // Handle "Select All" for acknowledged trips
  const handleSelectAllAck = (checked) => {
    if (checked) {
      setSelectedAcknowledged(tableData.acknowledged.map(item => item._id));
    } else {
      setSelectedAcknowledged([]);
    }
  };

  const handleGenerateBillClick = () => {
    if (selectedBilled.length === 0 && selectedAcknowledged.length === 0) {
      alert('Please select at least one trip to download the PDF');
      return;
    }
    setShowBillModal(true);
  };

  const handleGenerateBill = async (additionalRemarks) => {
    if (selectedBilled.length === 0 && selectedAcknowledged.length === 0) {
      alert('Please select at least one trip to download the PDF');
      setShowBillModal(false);
      return;
    }

    const billId = selectedBilled.length > 0 ? selectedBilled[0] : selectedAcknowledged[0];
    if (!billId) {
      alert('No valid Bill ID found for PDF generation');
      setShowBillModal(false);
      return;
    }

    // Check if trips are from the same vendor
    const billedSelectedRows = tableData.billed.filter(row => selectedBilled.includes(row._id));
    const billedUnselectedRows = tableData.billed.filter(row => !selectedBilled.includes(row._id));
    const ackSelectedRows = tableData.acknowledged.filter(row => selectedAcknowledged.includes(row._id));

    // Store the current bill_id for refetching later
    let currentBillId = '';

    // Try to get bill_id from selected rows
    if (billedSelectedRows.length > 0 && billedSelectedRows[0].billing_id) {
      currentBillId = billedSelectedRows[0].billing_id;
    } else if (ackSelectedRows.length > 0 && ackSelectedRows[0].billing_id) {
      currentBillId = ackSelectedRows[0].billing_id;
    }

    // Perform vendor check if needed
    const uniqueVendors = new Set();
    [...billedSelectedRows, ...ackSelectedRows].forEach(row => {
      if (row.vendor) uniqueVendors.add(row.vendor);
    });

    if (uniqueVendors.size > 1) {
      alert('Please select trips from only one vendor to generate the bill');
      setShowBillModal(false);
      setSelectedBilled([]);
      setSelectedAcknowledged([]);
      return;
    }

    // Create payload with additional parameters
    const payload = {
      billedSelectedRows,
      billedUnselectedRows,
      ackSelectedRows,
      additionalRemarks,
    };

    setIsDownloading(true); // start loading
    try {
      const response = await axios.post(downloadPdfUrl, payload, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob"
      });

      console.log("PDF response status:", response.status);

      if (response.status !== 200) {
        alert('Failed to generate PDF. Please try again later.');
        return;
      }

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `RevisedBill-${currentBillId || billId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert("Revised bill generated successfully.");

      setIsRefreshing(true);

      // Refetch data with the bill_id we stored earlier
      const refetchFilters = { bill_id: currentBillId || filters.bill_id };
      await fetchTableData(refetchFilters);

      setIsRefreshing(false);

      // Reset selections
      setSelectedBilled([]);
      setSelectedAcknowledged([]);
    } catch (error) {
      console.error("Error generating revised bill:", error);

      // Handle error responses that might be in blob format
      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorData = JSON.parse(await error.response.data.text());
          alert(`Error: ${errorData.message || "Failed to generate revised bill"}`);
        } catch (parseError) {
          alert("Failed to generate revised bill. Please try again.");
        }
      } else {
        alert("Failed to generate bill.");
      }
    } finally {
      setIsDownloading(false); // end loading
      setShowBillModal(false);
      setSelectedBilled([]);
      setSelectedAcknowledged([]);
    }
  };

  // Fixed click outside handler that properly ignores scrollbars and editing elements
  useEffect(() => {
    function handleClickOutside(event) {
      // Only check for clicks outside if we're in edit mode
      if (!editMode) return;

      // Don't close edit mode if clicked on these elements
      const isEditButton = event.target.closest('.edit-btn');
      const isEditFormElement = event.target.closest('.edit-form-container');
      const isEditControl = event.target.closest('.edit-controls');
      const isInput = event.target.tagName === 'INPUT' || event.target.tagName === 'SELECT';
      const isSaveButton = event.target.closest('.save-btn');

      // Also don't close if clicking on scrollbars
      const isScrollbar = (target) => {
        const { clientX, clientY } = event;
        const rect = target.getBoundingClientRect();

        // Check if the click is on the vertical scrollbar
        const isVerticalScrollbar =
          clientX > rect.right - 20 && // 20px is an approximate scrollbar width
          clientX <= rect.right &&
          clientY >= rect.top &&
          clientY <= rect.bottom &&
          target.scrollHeight > target.clientHeight;

        // Check if the click is on the horizontal scrollbar
        const isHorizontalScrollbar =
          clientY > rect.bottom - 20 && // 20px is an approximate scrollbar height 
          clientY <= rect.bottom &&
          clientX >= rect.left &&
          clientX <= rect.right &&
          target.scrollWidth > target.clientWidth;

        return isVerticalScrollbar || isHorizontalScrollbar;
      };

      // Check if the click was on a scrollbar
      const billedTable = document.querySelector('.billed-table');
      const acknowledgedTable = document.querySelector('.acknowledged-table');
      const tableBox = event.target.closest('.table-box');

      const clickedOnScrollbar =
        (billedTable && isScrollbar(billedTable.parentElement)) ||
        (acknowledgedTable && isScrollbar(acknowledgedTable.parentElement)) ||
        (tableBox && isScrollbar(tableBox));

      // If the click wasn't on an input/select/save button/scrollbar, exit edit mode
      if (!isEditButton && !isEditFormElement && !isEditControl && !isInput && !isSaveButton && !clickedOnScrollbar) {
        setEditingId(null);
        setEditingData({});
        setEditMode(false);
        setEditingType(null);
      }
    }

    // Add the event listener to the document body
    document.body.addEventListener('mousedown', handleClickOutside);

    // Clean up
    return () => {
      document.body.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editMode]);

  const handleEdit = (id, type) => {
    // If already editing something else, cancel that first
    if (editMode && editingId !== id) {
      setEditingId(null);
      setEditingData({});
    }

    const data = type === 'billed' ? tableData.billed : tableData.acknowledged;
    const rowData = data.find((item) => item._id === id);

    if (rowData) {
      setEditingId(id);
      setEditingData({ ...rowData });
      setEditMode(true);
      setEditingType(type);

      // Scroll to position of edited row with a slight delay to ensure DOM updates
      setTimeout(() => {
        const targetTable = type === 'billed'
          ? document.querySelector('.billed-table')
          : document.querySelector('.acknowledged-table');

        if (targetTable) {
          const editRow = targetTable.querySelector(`tr[data-id="${id}"]`) ||
            Array.from(targetTable.querySelectorAll('tr')).find(tr =>
              tr.textContent.includes(id.toString()));

          if (editRow) {
            // Scroll the row into view
            editRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);
    }
  };

  const handleEditingChange = (e) => {
    const { name, value } = e.target;
    setEditingData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async (id) => {
    try {
      // Store the bill_id for refetching
      const currentBillId = editingData.billing_id;

      // Optimistically update local state
      setTableData((prev) => {
        const updatedData = { ...prev };
        if (editingType === 'billed') {
          updatedData.billed = prev.billed.map((item) =>
            item._id === id ? { ...editingData } : item
          );
        } else if (editingType === 'acknowledged') {
          updatedData.acknowledged = prev.acknowledged.map((item) =>
            item._id === id ? { ...editingData } : item
          );
        }
        return updatedData;
      });

      // API call to update one revised bill
      const payload = {
        updateRows: editingData,
        trip_id: editingData.trip_id,
        bill_id: editingData.billing_id
      };

      console.log("Update Payload:", payload);

      const response = await axios.post(updateOneReviseBill, payload, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 201) {
        console.log("Success:", response.data.message);

        // Prepare filter for refetch
        const refetchFilters = { bill_id: currentBillId };

        // If we have date filters, use them as well
        if (filters.startDate && filters.endDate) {
          refetchFilters.startDate = filters.startDate;
          refetchFilters.endDate = filters.endDate;
        }

        console.log("Refetching with filters:", refetchFilters);

        // Refetch the data
        await fetchTableData(refetchFilters);

        alert(response.data.message);
      }

    } catch (error) {
      console.error("Update failed:", error);

      // Show detailed error message if available
      const errorMessage = error.response?.data?.message ||
        "Failed to update. Reverting changes...";
      alert(errorMessage);

      // Revert the optimistic update by refetching
      try {
        const refetchFilters = { bill_id: editingData.billing_id };
        if (filters.startDate && filters.endDate) {
          refetchFilters.startDate = filters.startDate;
          refetchFilters.endDate = filters.endDate;
        }
        await fetchTableData(refetchFilters);
      } catch (refetchError) {
        console.error("Refetch failed:", refetchError);
      }
    } finally {
      // Clean up edit state
      setEditingId(null);
      setEditingData({});
      setEditMode(false);
      setEditingType(null);
    }
  };

  // Function to handle keyboard navigation in edit mode
  const handleKeyDown = (e, id) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave(id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingId(null);
      setEditingData({});
      setEditMode(false);
      setEditingType(null);
    }
  };

  return (
    <div className='generate-revise-bill'>
      <div className="filter-box">
        <form onSubmit={handleSubmit}>
          <div className="filters-grid">
            <div className="filter-item">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-item">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-item">
              <label>Bill Id</label>
              <input
                type="text"
                name="bill_id"
                value={filters.bill_id}
                onChange={handleFilterChange}
                placeholder="Search by bill id: B20"
                pattern="B\d+"
                title="Bill ID must start with B followed by numbers (e.g. B20)"
              />
            </div>
          </div>

          <div className="button-group">
            <button type="submit" className="search-btn">Search</button>
            {/* Download PDF button - shown when there's data in either table */}
            {(tableData.billed.length > 0 || tableData.acknowledged.length > 0) && (
              <button
                type="button"
                className="download-btn"
                onClick={handleGenerateBillClick}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : 'Update Bill'}
              </button>
            )}
          </div>
        </form>

        {showBillModal && (
          <GenerateRevisedBillModal
            onClose={() => setShowBillModal(false)}
            onGenerate={handleGenerateBill}
          />
        )}

        {/* Only show tables after search is performed */}
        {(tableData.billed.length > 0 || tableData.acknowledged.length > 0 || noDataMessage) && (
          <div className="tables-container">
            {/* Billed Table - only show if there's data or message */}
            {(tableData.billed.length > 0 || noDataMessage) && (
              <div className="table-section">
                <div className="table-header-container">
                  <h2 className="table-title">Billed Trips</h2>
                  {filters.bill_id && tableData.billed.length > 0 && (
                    <div className="table-info-container">
                      {filters.bill_id && <h5 className="info-item">Remarks: {tableData.billed[0].remarks_amount}</h5>}
                      <hr style={{ width: "100%", borderTop: "0.12rem solid #ddd" }} />
                      <h4 className="info-item">Total Amount: {tableData.billed[tableData.billed.length - 1].final_amount}</h4>
                    </div>
                  )}
                </div>
                <div className="table-box">
                  <div className="table-responsive">
                    <table className="billed-table">
                      <thead>
                        <tr>
                          <th>
                            {tableData.billed.length > 0 && (
                              <div className="select-all-container">
                                {selectedBilled.length > 0 ? (
                                  <>
                                    <input
                                      type="checkbox"
                                      ref={selectAllBilledRef}
                                      checked={selectedBilled.length === tableData.billed.length && tableData.billed.length > 0}
                                      onChange={(e) => handleSelectAllBilled(e.target.checked)}
                                    />
                                    <span> Select All</span>
                                  </>) : (
                                  <span>Select</span>
                                )}
                              </div>
                            )}
                          </th>
                          <th>Trip ID</th>
                          <th>Username</th>
                          <th>Profile</th>
                          <th>Truck Number</th>
                          <th>Date</th>
                          {/* <th>Time</th> */}
                          <th>Vendor</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Wt.</th>
                          <th>A. Wt.</th>
                          <th>Freight</th>
                          <th>Rate</th>
                          <th>Diesel</th>
                          <th>Adv.</th>
                          <th>Toll</th>
                          <th>TDS</th>
                          <th>Short</th>
                          <th>GST Amount</th>
                          <th>Transaction</th>
                          <th>Amount</th>
                          <th>Bill ID</th>
                          <th>Edit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.billed.length === 0 ? (
                          <tr>
                            <td colSpan="20" style={{ textAlign: 'center', padding: '20px' }}>
                              No billed trips found
                            </td>
                          </tr>
                        ) : (
                          tableData.billed.map((row) => (
                            <tr key={`billed-${row._id}`}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedBilled.includes(row._id)}
                                  onChange={() => handleCheckboxChange(row._id, true)}
                                />
                              </td>
                              <td
                                onClick={async () => {
                                  let tripId = row.trip_id;
                                  setModuleChange('/home');
                                  const response = await axios.get(`/api/trip/${tripId}`, {
                                    withCredentials: true,
                                    headers: {
                                      "Content-Type": "application/json",
                                    }
                                  })
                                  console.log('TripData', response.data.TripData)
                                  setTripIDState(response.data.TripData);

                                  Navigate(`/home/${tripId}`);
                                }}
                                className='trip-id'
                              >{row.trip_id || null}</td>
                              <td>{row.username}</td>
                              <td>{row.profile}</td>
                              <td>{row.truck_no}</td>
                              <td>{row.date}</td>
                              {/* <td>{formatTime(row.time)}</td> */}
                              <td>{row.vendor}</td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="text"
                                      name="destination_from"
                                      value={editingData.destination_from || ""}
                                      onChange={handleEditingChange}
                                      onKeyDown={(e) => handleKeyDown(e, row._id)}
                                    />
                                  </div>
                                ) : (
                                  row.destination_from
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="text"
                                      name="destination_to"
                                      value={editingData.destination_to || ""}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.destination_to
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="weight"
                                      value={editingData.weight}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.weight
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="actual_weight"
                                      value={editingData.actual_weight}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.actual_weight
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="freight"
                                      value={editingData.freight}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.freight
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="rate"
                                      value={editingData.rate}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.rate
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="diesel"
                                      value={editingData.diesel}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.diesel
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="advance"
                                      value={editingData.advance}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.advance
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="toll"
                                      value={editingData.toll}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.toll
                                )}
                              </td>
                              <td>
                                {/* {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="tds"
                                      value={editingData.tds}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.tds
                                )} */}
                                {row.tds}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="short"
                                      value={editingData.short}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.short
                                )}
                              </td>
                              <td>{row.gst_amount}</td>
                              <td>
                                {/* {editingId === row._id ? (
                                  <select
                                    name="transaction_status"
                                    value={editingData.transaction_status || ""}
                                    onChange={handleEditingChange}
                                  > TransactionStatus
                                    <option value="Billed">Billed</option>
                                    <option value="Acknowledged">Acknowledged</option>
                                  </select>
                                ) : (
                                  row.transaction_status
                                )} */}
                                {row.transaction_status}
                              </td>
                              <td>{row.amount}</td>
                              <td>{row.billing_id}</td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container edit-controls">
                                    <button
                                      className="save-btn"
                                      onClick={() => handleSave(row._id)}
                                    >
                                      Save
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="edit-btn"
                                    onClick={() => handleEdit(row._id, 'billed')}
                                  >
                                    Edit
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Acknowledged Table - only show if there's data or message */}
            {(tableData.acknowledged.length > 0 || noDataMessage) && (
              <div className="table-section">
                <h2 className="table-title">Acknowledged Trips</h2>
                <div className="table-box">
                  <div className="table-responsive">
                    <table className="acknowledged-table">
                      <thead>
                        <tr>
                          <th>
                            {tableData.acknowledged.length > 0 && (
                              <div className="select-all-container">
                                {selectedAcknowledged.length > 0 ? (
                                  <>
                                    <input
                                      type="checkbox"
                                      ref={selectAllAckRef}
                                      checked={selectedAcknowledged.length === tableData.acknowledged.length}
                                      onChange={(e) => handleSelectAllAck(e.target.checked)}
                                    />
                                    <span> Select All</span>
                                  </>
                                ) : (
                                  <span>Select</span>
                                )}
                              </div>
                            )}
                          </th>
                          <th>Trip ID</th>
                          <th>Username</th>
                          <th>Profile</th>
                          <th>Truck Number</th>
                          <th>Date</th>
                          {/* <th>Time</th> */}
                          <th>Vendor</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Weight</th>
                          <th>Actual Weight</th>
                          <th>Freight</th>
                          <th>Rate</th>
                          <th>Diesel</th>
                          <th>Advance</th>
                          <th>Toll</th>
                          <th>TDS Amount</th>
                          <th>Short</th>
                          <th>T.Status</th>
                          <th>Amount</th>
                          <th>Bill ID</th>
                          <th>Edit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.acknowledged.length === 0 ? (
                          <tr>
                            <td colSpan="20" style={{ textAlign: 'center', padding: '20px' }}>
                              No acknowledged trips found
                            </td>
                          </tr>
                        ) : (
                          tableData.acknowledged.map((row) => (
                            <tr key={`acknowledged-${row._id}`}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedAcknowledged.includes(row._id)}
                                  onChange={() => handleCheckboxChange(row._id, false)}
                                />
                              </td>
                              <td
                                onClick={async () => {
                                  let tripId = row.trip_id;
                                  setModuleChange('/home');
                                  const response = await axios.get(`/api/trip/${tripId}`, {
                                    withCredentials: true,
                                    headers: {
                                      "Content-Type": "application/json",
                                    }
                                  })
                                  console.log('TripData', response.data.TripData)
                                  setTripIDState(response.data.TripData);

                                  Navigate(`/home/${tripId}`);
                                }}
                                className='trip-id'
                              >{row.trip_id || null}</td>
                              <td>{row.username}</td>
                              <td>{row.profile}</td>
                              <td>{row.truck_no}</td>
                              <td>{row.date}</td>
                              {/* <td>{formatTime(row.time)}</td> */}
                              <td>{row.vendor}</td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="text"
                                      name="destination_from"
                                      value={editingData.destination_from || ""}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.destination_from
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="text"
                                      name="destination_to"
                                      value={editingData.destination_to || ""}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.destination_to
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="weight"
                                      value={editingData.weight}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.weight
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="actual_weight"
                                      value={editingData.actual_weight}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.actual_weight
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="freight"
                                      value={editingData.freight}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.freight
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="rate"
                                      value={editingData.rate}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.rate
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="diesel"
                                      value={editingData.diesel}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.diesel
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="advance"
                                      value={editingData.advance}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.advance
                                )}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="toll"
                                      value={editingData.toll}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.toll
                                )}
                              </td>
                              <td>
                                {/* {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="tds"
                                      value={editingData.tds}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.tds
                                )} */}
                                {row.tds}
                              </td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container">
                                    <input
                                      type="number"
                                      name="short"
                                      value={editingData.short}
                                      onChange={handleEditingChange}
                                    />
                                  </div>
                                ) : (
                                  row.short
                                )}
                              </td>
                              <td>
                                {/* {editingId === row._id ? (
                                  <select
                                    name="transaction_status"
                                    value={editingData.transaction_status || ""}
                                    onChange={handleEditingChange}
                                  >
                                    <option value="Billed">Billed</option>
                                    <option value="Acknowledged">Acknowledged</option>
                                  </select>
                                ) : ( 
                                  row.transaction_status
                                 )} */}
                                {row.transaction_status}
                              </td>
                              <td>{row.amount}</td>
                              <td>{row.billing_id}</td>
                              <td>
                                {editingId === row._id ? (
                                  <div className="edit-form-container edit-controls">
                                    <button
                                      className="save-btn"
                                      onClick={() => handleSave(row._id)}
                                    >
                                      Save
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="edit-btn"
                                    onClick={() => handleEdit(row._id, 'acknowledged')}
                                  >
                                    Edit
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReviseBill;